import "../Overview/EndpointOverview.css";
import "../CloudAnalytics/CloudEntityAnalytics.css";
import "./ActiveDirectory.css";
import { useState, useMemo, useEffect } from "react";
import { Card } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
    Database,
    Users,
    Monitor,
    Building2,
    Shield,
    Search,
    ChevronRight,
    ChevronDown,
    ChevronLeft,
    ChevronsLeft,
    ChevronsRight,
    User,
    Layers,
    AlertTriangle,
    Crown,
    Settings,
    X,
    Loader2,
    UserPlus,
    UserMinus,
    type LucideIcon,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useToast } from "../../../hooks/use-toast";
import { getUsers, getDevices, organizationUnits, getUserGroupMemberships, getOUUsers, getOUDevices, updateUserGroupMembership } from "./Api/activedirectoryapi";
import { myLocalStorage } from "../../StorageHelper";
import { ADDevice, ADDomain, ADOrganizationUnit, ADUser, UserGroupMembership } from "./interface";
import { useNavigate } from "react-router-dom";




export function extractDomainFromDN(
    dn: string,
    uniqueDomains: Set<string> // pass a Set for O(1) inserts & uniqueness
): string | null {
    if (!dn) return null;

    const matches = dn.match(/DC=([^,]+)/gi);
    if (!matches) return null;

    const domainParts = matches.map(dc => dc.split("=")[1].trim());
    const domain = domainParts.join(".");

    // Insert domain into the unique Set
    uniqueDomains.add(domain);

    return domain;
}

export function buildDomainTree(domains: string[]): Record<string, any> {
    const root: Record<string, any> = {};

    for (const domain of domains) {
        const parts = domain.split("."); // ["team","region","corp","local"]
        const reversedParts = parts.reverse(); // ["local","corp","region","team"]

        let pointer = root;

        for (const part of reversedParts) {
            if (!pointer[part]) {
                pointer[part] = {};
            }
            pointer = pointer[part];
        }
    }

    return root;
}

/**
 * Convenience function:
 * Takes the entire response array and returns:
 *   { domainsList: string[], tree: object }
 */
export function processUsersForDomains(
    users: Array<{ DistinguishedName?: string }>
) {
    const uniqueDomains = new Set<string>();

    for (const user of users) {
        if (user?.DistinguishedName) {
            extractDomainFromDN(user.DistinguishedName, uniqueDomains);
        }
    }

    const domainsList = Array.from(uniqueDomains);
    const tree = buildDomainTree(domainsList);

    return { domainsList, tree };
}


type AdTone = "primary" | "success" | "danger" | "warning";

function AdDialogHeader({
    icon: Icon,
    tone = "primary",
    title,
    subtitle,
    detail,
}: {
    icon: LucideIcon;
    tone?: AdTone;
    title: string;
    subtitle?: string;
    detail?: string;
}) {
    return (
        <DialogHeader className="ad-dialog-header">
            <div className="ad-dialog-heading">
                <span className="ad-dialog-icon" data-tone={tone} aria-hidden="true">
                    <Icon />
                </span>
                <div className="min-w-0">
                    <DialogTitle className="ad-dialog-title">{title}</DialogTitle>
                    {subtitle && <DialogDescription className="ad-dialog-subtitle">{subtitle}</DialogDescription>}
                </div>
            </div>
            {detail && <span className="ad-dialog-count">{detail}</span>}
        </DialogHeader>
    );
}

function AdSearchInput({
    value,
    onChange,
    placeholder,
    testId,
}: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    testId: string;
}) {
    return (
        <div className="ad-dialog-toolbar">
            <label className="ad-field">
                <span className="ad-field-label"><Search aria-hidden="true" />Search</span>
                <span className="ad-search">
                    <Search className="ad-search-icon" aria-hidden="true" />
                    <Input
                        placeholder={placeholder}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        data-testid={testId}
                    />
                </span>
            </label>
        </div>
    );
}

function AdState({
    icon: Icon,
    loading = false,
    title,
    message,
    compact = false,
}: {
    icon?: LucideIcon;
    loading?: boolean;
    title?: string;
    message: string;
    compact?: boolean;
}) {
    return (
        <div className="ad-state" data-compact={compact} role={loading ? "status" : undefined}>
            <span className="ad-state-icon" aria-hidden="true">
                {loading ? <Loader2 className="animate-spin" /> : Icon && <Icon />}
            </span>
            {title && <p className="ad-state-title">{title}</p>}
            <p className="ad-state-message">{message}</p>
        </div>
    );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function ActiveDirectoryManagement() {
    const latestTenant = useMemo(() => {
        const tenant = myLocalStorage.getItem("latestTenant");
        return tenant?.tenantName || "";
    }, []);

    const [adUsers, setAdUsers] = useState<ADUser[]>([]);
    const [adDevices, setAdDevices] = useState<ADDevice[]>([]);
    const [adOUs, setAdOUs] = useState<ADOrganizationUnit[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [isLoadingOUs, setIsLoadingOUs] = useState(false);

    const [selectedDomain, setSelectedDomain] = useState<ADDomain | null>(null);
    const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set(['contoso']));
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [activeTab, setActiveTab] = useState("services");
    const [isDomainSidebarCollapsed, setIsDomainSidebarCollapsed] = useState(false);
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<string>("");
    const [selectedGroupsList, setSelectedGroupsList] = useState<string[]>([]);
    const [groupUserSearch, setGroupUserSearch] = useState("");
    const [usersPage, setUsersPage] = useState(1);
    const [usersPageSize, setUsersPageSize] = useState(25);
    const [servicesPage, setServicesPage] = useState(1);
    const [servicesPageSize, setServicesPageSize] = useState(25);
    const [devicesPage, setDevicesPage] = useState(1);
    const [devicesPageSize, setDevicesPageSize] = useState(25);
    const [groupsPage, setGroupsPage] = useState(1);
    const [groupsPageSize, setGroupsPageSize] = useState(25);
    const [ousPage, setOusPage] = useState(1);
    const [ousPageSize, setOusPageSize] = useState(25);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedGroupMembership, setSelectedGroupMembership] = useState<UserGroupMembership | null>(null);
    const [addUserToGroupModalOpen, setAddUserToGroupModalOpen] = useState(false);
    const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);
    const [selectedMemberToRemove, setSelectedMemberToRemove] = useState<string>("");
    const [userSearchForGroup, setUserSearchForGroup] = useState("");
    const [confirmAddUserModalOpen, setConfirmAddUserModalOpen] = useState(false);
    const [selectedUserToAdd, setSelectedUserToAdd] = useState<ADUser | null>(null);
    const [userGroupsModalOpen, setUserGroupsModalOpen] = useState(false);
    const [selectedUserForGroups, setSelectedUserForGroups] = useState<ADUser | null>(null);
    const [userGroupMemberships, setUserGroupMemberships] = useState<UserGroupMembership[]>([]);
    const [userGroupsSearch, setUserGroupsSearch] = useState("");
    const [showGroupMembersModalOpen, setShowGroupMembersModalOpen] = useState(false);
    const [groupMembersSearch, setGroupMembersSearch] = useState("");
    const [ouUsersModalOpen, setOuUsersModalOpen] = useState(false);
    const [selectedOU, setSelectedOU] = useState<ADOrganizationUnit | null>(null);
    const [ouUsers, setOuUsers] = useState<ADUser[]>([]);
    const [ouUsersSearch, setOuUsersSearch] = useState("");

    const [ouDevicesModalOpen, setOuDevicesModalOpen] = useState(false);
    const [selectedOUForDevices, setSelectedOUForDevices] = useState<ADOrganizationUnit | null>(null);
    const [ouDevices, setOuDevices] = useState<ADDevice[]>([]);
    const [ouDevicesSearch, setOuDevicesSearch] = useState("");
    const [isAddingUser, setIsAddingUser] = useState(false);
    const [isRemovingUser, setIsRemovingUser] = useState(false);

    const navigate = useNavigate();

    const { toast } = useToast();

    useEffect(() => {
        const fetchADUsers = async () => {
            if (!latestTenant) return;
            setIsLoadingUsers(true);
            try {
                const data = await getUsers(latestTenant);
                setAdUsers(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching AD users:", error);
                toast({
                    title: "Error",
                    description: "Failed to load Active Directory users",
                    variant: "destructive"
                });
                setAdUsers([]);
            } finally {
                setIsLoadingUsers(false);
            }
        };
        fetchADUsers();
    }, [latestTenant]);

    useEffect(() => {
        const fetchADDevices = async () => {
            if (!latestTenant) return;
            setIsLoadingDevices(true);
            try {
                const data = await getDevices(latestTenant);
                setAdDevices(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching AD devices:", error);
                toast({
                    title: "Error",
                    description: "Failed to load Active Directory devices",
                    variant: "destructive"
                });
                setAdDevices([]);
            } finally {
                setIsLoadingDevices(false);
            }
        };
        fetchADDevices();
    }, [latestTenant]);

    useEffect(() => {
        const fetchOUs = async () => {
            if (!latestTenant) return;
            setIsLoadingOUs(true);
            try {
                const data = await organizationUnits(latestTenant);
                setAdOUs(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching organization units:", error);
                toast({
                    title: "Error",
                    description: "Failed to load organization units",
                    variant: "destructive"
                });
                setAdOUs([]);
            } finally {
                setIsLoadingOUs(false);
            }
        };
        fetchOUs();
    }, [latestTenant]);

    useEffect(() => {
        const fetchGroupMemberships = async () => {
            if (!latestTenant) return;
            if (adUsers.length === 0) return;
            try {
                const data = await getUserGroupMemberships(latestTenant, adUsers[0].id || -1);
                setUserGroupMemberships(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching group memberships:", error);
                toast({
                    title: "Error",
                    description: "Failed to load group memberships",
                    variant: "destructive"
                });
                setUserGroupMemberships([]);
            }
        };
        fetchGroupMemberships();
    }, [latestTenant, adUsers]);

    useEffect(() => {
        setIsSearching(true);
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setUsersPage(1);
            setServicesPage(1);
            setDevicesPage(1);
            setGroupsPage(1);
            setOusPage(1);
            setIsSearching(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const toggleDomainExpansion = (domainId: string) => {
        const newExpanded = new Set(expandedDomains);
        if (newExpanded.has(domainId)) {
            newExpanded.delete(domainId);
        } else {
            newExpanded.add(domainId);
        }
        setExpandedDomains(newExpanded);
    };

    const generatedDomains = useMemo(() => {
        const uniqueDomains = new Set<string>();
        const allItems = [...adUsers, ...adDevices];

        for (const item of allItems) {
            if ('DistinguishedName' in item && item.DistinguishedName) {
                extractDomainFromDN(item.DistinguishedName, uniqueDomains);
            }
        }

        const domainsList = Array.from(uniqueDomains);

        return domainsList.map((fqdn) => {
            // Extract DC parts for exact matching (e.g., DC=WSAD2,DC=local)

            // Calculate counts specific to this domain by matching DC parts exactly
            const domainUsers = adUsers.filter(u => {
                const userDomain = extractDomainFromDN(u.DistinguishedName, new Set());
                return userDomain === fqdn;
            });
            if (domainUsers.length === 0) {
                return undefined;
            }

            const domainDevices = adDevices.filter(d => {
                const deviceDomain = extractDomainFromDN(d.DistinguishedName, new Set());
                return deviceDomain === fqdn;
            });

            const domainOUs = adOUs.filter(ou => {
                const ouDN = ou.distinguishedName || '';
                const ouDomain = extractDomainFromDN(ouDN, new Set());
                return ouDomain === fqdn;
            });

            return {
                id: fqdn,
                name: fqdn,
                fqdn,
                type: 'root' as const,
                parent: undefined,
                userCount: domainUsers.length,
                deviceCount: domainDevices.length,
                serviceAccountCount: domainUsers.filter(u => u.isServiceAccount).length,
                groupCount: 0,
                ouCount: domainOUs.length
            };
        });
    }, [adUsers, adDevices, adOUs]); const isSingleDomainSetup = useMemo(() => {
        const rootDomains = generatedDomains.filter(d => d?.type === 'root');
        if (rootDomains.length !== 1) return false;
        const children = generatedDomains.filter(d => d?.parent === rootDomains[0]?.id);
        return children.length === 0;
    }, [generatedDomains]);

    useEffect(() => {
        if (!selectedDomain && generatedDomains.length > 0) {
            setSelectedDomain(generatedDomains[0] || null);
        }
    }, [generatedDomains, selectedDomain]);

    const renderDomainTree = (domain: ADDomain, level: number = 0) => {
        const children = generatedDomains.filter(d => d?.parent === domain?.id);
        const isExpanded = expandedDomains.has(domain.id);
        const isSelected = selectedDomain?.id === domain.id;

        if (isSingleDomainSetup && level === 0) {
            return (
                <div key={domain.id}>
                    <div className="ad-domain-card" data-selected="true" data-testid={`domain-${domain.id}`}>
                        <span className="ad-domain-icon" aria-hidden="true">
                            <Database />
                        </span>
                        <div className="ad-domain-copy">
                            <div className="ad-domain-name">{domain.name}</div>
                            <div className="ad-domain-meta">
                                {domain.userCount.toLocaleString()} users • {domain.serviceAccountCount.toLocaleString()} services
                            </div>
                        </div>
                        <Crown className="ad-domain-crown" aria-label="Root domain" />
                    </div>
                </div>
            );
        }

        return (
            <div key={domain.id}>
                <div
                    className="ad-domain-card"
                    data-selected={isSelected}
                    data-interactive="true"
                    style={{ marginLeft: `${level * 16}px` }}
                    onClick={() => setSelectedDomain(domain)}
                    data-testid={`domain-${domain.id}`}
                >
                    {children.length > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="ad-domain-toggle"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleDomainExpansion(domain.id);
                            }}
                            aria-label={isExpanded ? `Collapse ${domain.name}` : `Expand ${domain.name}`}
                        >
                            {isExpanded ? <ChevronDown /> : <ChevronRight />}
                        </Button>
                    )}
                    <span className="ad-domain-icon" aria-hidden="true">
                        <Database />
                    </span>
                    <div className="ad-domain-copy">
                        <div className="ad-domain-name">{domain.name}</div>
                        <div className="ad-domain-meta">
                            {domain.userCount.toLocaleString()} users • {domain.serviceAccountCount.toLocaleString()} services
                        </div>
                    </div>
                    {domain.type === 'root' && (
                        <Crown className="ad-domain-crown" aria-label="Root domain" />
                    )}
                </div>
                {isExpanded && children.map(child => child && renderDomainTree(child, level + 1))}
            </div>
        );
    };

    const filteredUsers = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return adUsers.filter(user => {
            if (user.isServiceAccount) return false;
            if (selectedDomain) {
                const userDomain = extractDomainFromDN(user.DistinguishedName, new Set());
                if (userDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (user.Name || '').toLowerCase();
            const sam = (user.SamAccountName || '').toLowerCase();
            const upn = (user.UserPrincipalName || '').toLowerCase();
            const email = (user.EmailAddress || '').toLowerCase();
            const dn = (user.DistinguishedName || '').toLowerCase();
            const id = String(user.id || '').toLowerCase();
            const mobile = (user.MobilePhone || '').toLowerCase();
            return (
                name.includes(query) ||
                sam.includes(query) ||
                upn.includes(query) ||
                email.includes(query) ||
                dn.includes(query) ||
                id.includes(query) ||
                mobile.includes(query)
            );
        });
    }, [debouncedSearch, adUsers, selectedDomain]);

    const filteredServiceAccounts = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return adUsers.filter(account => {
            if (!account.isServiceAccount) return false;
            if (selectedDomain) {
                const accountDomain = extractDomainFromDN(account.DistinguishedName, new Set());
                if (accountDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (account.Name || '').toLowerCase();
            const sam = (account.SamAccountName || '').toLowerCase();
            const upn = (account.UserPrincipalName || '').toLowerCase();
            const email = (account.EmailAddress || '').toLowerCase();
            const dn = (account.DistinguishedName || '').toLowerCase();
            const id = String(account.id || '').toLowerCase();
            const mobile = (account.MobilePhone || '').toLowerCase();
            return (
                name.includes(query) ||
                sam.includes(query) ||
                upn.includes(query) ||
                email.includes(query) ||
                dn.includes(query) ||
                id.includes(query) ||
                mobile.includes(query)
            );
        });
    }, [debouncedSearch, adUsers, selectedDomain]);

    const filteredDevices = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return adDevices.filter(device => {
            if (selectedDomain) {
                const deviceDomain = extractDomainFromDN(device.DistinguishedName, new Set());
                if (deviceDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (device.Name || '').toLowerCase();
            const dns = (device.DNSHostName || '').toLowerCase();
            const os = (device.OperatingSystem || '').toLowerCase();
            const ip = (device.IPv4Address || '').toLowerCase();
            const dn = (device.DistinguishedName || '').toLowerCase();
            const endpointId = (device.endpointId || '').toLowerCase();
            return (
                name.includes(query) ||
                dns.includes(query) ||
                os.includes(query) ||
                ip.includes(query) ||
                dn.includes(query) ||
                endpointId.includes(query)
            );
        });
    }, [debouncedSearch, adDevices, selectedDomain]);


    const filteredGroups = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return userGroupMemberships.filter(membership => {
            if (selectedDomain) {
                const groupDomain = extractDomainFromDN(membership.adUserGroup.DistinguishedName, new Set());
                if (groupDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (membership.adUserGroup.Name || '').toLowerCase();
            const sam = (membership.adUserGroup.SamAccountName || '').toLowerCase();
            const dn = (membership.adUserGroup.DistinguishedName || '').toLowerCase();
            const cn = (membership.adUserGroup.cn || '').toLowerCase();
            const id = String(membership.adUserGroup.id || '').toLowerCase();
            const membersJoined = membership.adUserGroup.Members.join(' ').toLowerCase();
            return (
                name.includes(query) ||
                sam.includes(query) ||
                dn.includes(query) ||
                cn.includes(query) ||
                id.includes(query) ||
                membersJoined.includes(query)
            );
        });
    }, [debouncedSearch, userGroupMemberships, selectedDomain]);


    const filteredOUs = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return adOUs.filter(ou => {
            if (selectedDomain) {
                const ouDomain = extractDomainFromDN(ou.distinguishedName || '', new Set());
                if (ouDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (ou.name || '').toLowerCase();
            const cn = (ou.cn || '').toLowerCase();
            const dn = (ou.distinguishedName || '').toLowerCase();
            const id = String(ou.id || '').toLowerCase();
            return (
                name.includes(query) ||
                cn.includes(query) ||
                dn.includes(query) ||
                id.includes(query)
            );
        });
    }, [debouncedSearch, adOUs, selectedDomain]);

    const paginatedUsers = useMemo(() => {
        const start = (usersPage - 1) * usersPageSize;
        return filteredUsers.slice(start, start + usersPageSize);
    }, [filteredUsers, usersPage, usersPageSize]);

    const paginatedServiceAccounts = useMemo(() => {
        const start = (servicesPage - 1) * servicesPageSize;
        return filteredServiceAccounts.slice(start, start + servicesPageSize);
    }, [filteredServiceAccounts, servicesPage, servicesPageSize]);

    const paginatedDevices = useMemo(() => {
        const start = (devicesPage - 1) * devicesPageSize;
        return filteredDevices.slice(start, start + devicesPageSize);
    }, [filteredDevices, devicesPage, devicesPageSize]);

    const paginatedGroups = useMemo(() => {
        const start = (groupsPage - 1) * groupsPageSize;
        return filteredGroups.slice(start, start + groupsPageSize);
    }, [filteredGroups, groupsPage, groupsPageSize]);

    const paginatedOUs = useMemo(() => {
        const start = (ousPage - 1) * ousPageSize;
        return filteredOUs.slice(start, start + ousPageSize);
    }, [filteredOUs, ousPage, ousPageSize]);

    const totalUsersPages = Math.ceil(filteredUsers.length / usersPageSize);
    const totalServicesPages = Math.ceil(filteredServiceAccounts.length / servicesPageSize);
    const totalDevicesPages = Math.ceil(filteredDevices.length / devicesPageSize);
    const totalGroupsPages = Math.ceil(filteredGroups.length / groupsPageSize);
    const totalOUsPages = Math.ceil(filteredOUs.length / ousPageSize);

    const extractCNFromDN = (dn: string): string => {
        const cnMatch = dn.match(/^CN=([^,]+)/);
        return cnMatch ? cnMatch[1] : dn;
    };


    const getUserGroupsForUser = (user: ADUser): UserGroupMembership[] => {
        const userNameLower = (user.Name || "").toLowerCase();
        const samLower = (user.SamAccountName || "").toLowerCase();
        const dnLower = (user.DistinguishedName || "").toLowerCase();
        const cnLower = extractCNFromDN(user.DistinguishedName || "").toLowerCase();
        const emailLower = (user.EmailAddress || "").toLowerCase();
        const upnLower = (user.UserPrincipalName || "").toLowerCase();

        const identifiers = [dnLower, cnLower, samLower, userNameLower, emailLower, upnLower].filter(Boolean);

        return userGroupMemberships.filter(m => {
            const memberMatch = m.adUserGroup.Members.some(memberDN => {
                const memberLower = (memberDN || "").toLowerCase();
                return identifiers.some(id => memberLower.includes(id));
            });

            const adUser = m.adUser;
            const adUserDN = (adUser?.DistinguishedName || "").toLowerCase();
            const adUserSam = (adUser?.SamAccountName || "").toLowerCase();
            const adUserName = (adUser?.Name || "").toLowerCase();
            const adUserUpn = (adUser?.UserPrincipalName || "").toLowerCase();
            const adUserEmail = (adUser?.EmailAddress || "").toLowerCase();

            const recordMatch = identifiers.some(id =>
                adUserDN.includes(id) ||
                adUserSam.includes(id) ||
                adUserName.includes(id) ||
                (adUserUpn && adUserUpn.includes(id)) ||
                (adUserEmail && adUserEmail.includes(id))
            );

            return memberMatch || recordMatch;
        });
    };

    const handleAddUserToGroup = async (groupId: number, user: ADUser) => {
        if (!latestTenant) return;

        setIsAddingUser(true);
        try {
            await updateUserGroupMembership(latestTenant, {
                userId: user.id,
                groupId: groupId,
                groupStatus: "ENABLED",
                selectedTime: 3600
            });

            const userDN = user.DistinguishedName;
            setUserGroupMemberships(prev => {
                const updated = prev.map(membership => {
                    if (membership.adUserGroup.id === groupId) {
                        if (!membership.adUserGroup.Members.includes(userDN)) {
                            const newMembership = {
                                ...membership,
                                adUserGroup: {
                                    ...membership.adUserGroup,
                                    Members: [...membership.adUserGroup.Members, userDN]
                                }
                            };
                            if (selectedGroupMembership?.adUserGroup.id === groupId) {
                                setSelectedGroupMembership(newMembership);
                            }
                            return newMembership;
                        }
                    }
                    return membership;
                });
                return updated;
            });

            toast({
                title: "User Added",
                description: `${user.Name} has been added to the group successfully.`,
            });
            setAddUserToGroupModalOpen(false);
            setUserSearchForGroup("");
        } catch (error) {
            console.error("Error adding user to group:", error);
            toast({
                title: "Error",
                description: "Failed to add user to group. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsAddingUser(false);
        }
    };

    const handleRemoveUserFromGroup = async (groupId: number, memberDN: string) => {
        if (!latestTenant) return;

        // Find the user by their Distinguished Name
        const user = adUsers.find(u => u.DistinguishedName === memberDN);
        if (!user) {
            toast({
                title: "Error",
                description: "User not found.",
                variant: "destructive"
            });
            return;
        }

        setIsRemovingUser(true);
        try {
            await updateUserGroupMembership(latestTenant, {
                userId: user.id,
                groupId: groupId,
                groupStatus: "DISABLED",
                selectedTime: 3600
            });

            setUserGroupMemberships(prev => {
                const updated = prev.map(membership => {
                    if (membership.adUserGroup.id === groupId) {
                        const newMembership = {
                            ...membership,
                            adUserGroup: {
                                ...membership.adUserGroup,
                                Members: membership.adUserGroup.Members.filter(m => m !== memberDN)
                            }
                        };
                        if (selectedGroupMembership?.adUserGroup.id === groupId) {
                            setSelectedGroupMembership(newMembership);
                        }
                        return newMembership;
                    }
                    return membership;
                });
                return updated;
            });

            toast({
                title: "User Removed",
                description: `User has been removed from the group successfully.`,
            });
            setRemoveUserModalOpen(false);
            setSelectedMemberToRemove("");
        } catch (error) {
            console.error("Error removing user from group:", error);
            toast({
                title: "Error",
                description: "Failed to remove user from group. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsRemovingUser(false);
        }
    };


    const handleShowOUUsers = async (ou: ADOrganizationUnit) => {
        setSelectedOU(ou);
        setOuUsersSearch("");
        setOuUsersModalOpen(true);
        setOuUsers([]); // Clear previous data

        try {
            const users = await getOUUsers(ou.id);
            setOuUsers(Array.isArray(users) ? users : []);
        } catch (error) {
            console.error("Error fetching OU users:", error);
            toast({
                title: "Error",
                description: `Failed to load users for ${ou.name}`,
                variant: "destructive"
            });
            setOuUsers([]);
        }
    };


    const handleShowOUDevices = async (ou: ADOrganizationUnit) => {
        setSelectedOUForDevices(ou);
        setOuDevicesSearch("");
        setOuDevicesModalOpen(true);
        setOuDevices([]); // Clear previous data

        try {
            const devices = await getOUDevices(ou.id);
            setOuDevices(Array.isArray(devices) ? devices : []);
        } catch (error) {
            console.error("Error fetching OU devices:", error);
            toast({
                title: "Error",
                description: `Failed to load devices for ${ou.name}`,
                variant: "destructive"
            });
            setOuDevices([]);
        }
    };

    const getPageNumbers = (currentPage: number, totalPages: number) => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                pages.push(1, 2, 3, 4, '...', totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }
        return pages;
    };

    const renderPaginationControls = (
        currentPage: number,
        totalPages: number,
        totalItems: number,
        pageSize: number,
        setPage: (page: number) => void,
        setPageSize: (size: number) => void,
        entityName: string
    ) => {
        const startItem = (currentPage - 1) * pageSize + 1;
        const endItem = Math.min(currentPage * pageSize, totalItems);
        const pageNumbers = getPageNumbers(currentPage, totalPages);

        return (
            <div className="ad-pagination">
                <div className="ad-pagination-meta">
                    <span className="ad-pagination-summary">
                        Showing <strong>{totalItems > 0 ? startItem : 0}</strong>–<strong>{endItem}</strong> of{' '}
                        <strong>{totalItems.toLocaleString()}</strong> {entityName}
                    </span>
                    <div className="ad-page-size">
                        <span>Per page</span>
                        <Select value={pageSize.toString()} onValueChange={(val) => { setPageSize(Number(val)); setPage(1); }}>
                            <SelectTrigger className="ad-page-size-trigger" data-testid={`select-pagesize-${entityName}`} aria-label={`${entityName} per page`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PAGE_SIZE_OPTIONS.map(size => (
                                    <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <nav className="ad-pagination-nav" aria-label={`${entityName} pages`}>
                    <Button
                        variant="outline"
                        size="icon"
                        className="ad-page-button"
                        onClick={() => setPage(1)}
                        disabled={currentPage === 1}
                        aria-label="First page"
                        data-testid={`button-first-${entityName}`}
                    >
                        <ChevronsLeft />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="ad-page-button"
                        onClick={() => setPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                        data-testid={`button-prev-${entityName}`}
                    >
                        <ChevronLeft />
                    </Button>

                    {pageNumbers.map((page, idx) => (
                        typeof page === 'number' ? (
                            <Button
                                key={idx}
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                className="ad-page-button"
                                onClick={() => setPage(page)}
                                aria-current={currentPage === page ? "page" : undefined}
                                data-testid={`button-page-${page}-${entityName}`}
                            >
                                {page}
                            </Button>
                        ) : (
                            <span key={idx} className="ad-page-ellipsis" aria-hidden="true">…</span>
                        )
                    ))}

                    <Button
                        variant="outline"
                        size="icon"
                        className="ad-page-button"
                        onClick={() => setPage(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        aria-label="Next page"
                        data-testid={`button-next-${entityName}`}
                    >
                        <ChevronRight />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="ad-page-button"
                        onClick={() => setPage(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        aria-label="Last page"
                        data-testid={`button-last-${entityName}`}
                    >
                        <ChevronsRight />
                    </Button>
                </nav>
            </div>
        );
    };


    const tabItems = [
        { value: "services", label: "Service Accounts", icon: Settings, count: filteredServiceAccounts.length },
        { value: "users", label: "Users", icon: Users, count: filteredUsers.length },
        { value: "groups", label: "Groups", icon: Shield, count: filteredGroups.length, testId: "tab-groups" },
        { value: "ous", label: "Organization Units", icon: Layers, count: filteredOUs.length, testId: "tab-ous" },
        { value: "devices", label: "Devices", icon: Monitor, count: filteredDevices.length },
    ];

    const renderAccountRows = (accounts: ADUser[], variant: "user" | "service") => (
        <div className="ad-rows">
            {accounts.map((account) => {
                const accountGroups = getUserGroupsForUser(account);
                const isService = variant === "service";
                const AccountIcon = isService ? Settings : User;
                return (
                    <div
                        key={account.id}
                        className="ad-row ad-grid-accounts"
                        data-interactive="true"
                        onClick={() => navigate(isService
                            ? `/activeDirectory/service/${account.id}/${account.Name}`
                            : `/activeDirectory/user/${account.id}/${account.Name}/${account.SamAccountName}`)}
                        data-testid={isService ? `row-service-${account.id}` : `link-user-${account.id}`}
                    >
                        <div className="ad-identity">
                            <span className="ad-row-icon" aria-hidden="true">
                                <AccountIcon />
                            </span>
                            <div className="min-w-0">
                                <div className="ad-identity-name">{account.Name}</div>
                                <div className="ad-identity-sub">{account.SamAccountName}</div>
                            </div>
                        </div>
                        <div className="ad-cell-muted">{account.EmailAddress || '-'}</div>
                        <div className="ad-cell-mono" title={account.DistinguishedName}>{account.DistinguishedName}</div>
                        <div className="ad-cell-end">
                            <Button
                                variant="outline"
                                size="sm"
                                className="ad-count-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedUserForGroups(account);
                                    setUserGroupsModalOpen(true);
                                }}
                                aria-label={`View ${accountGroups.length} group memberships for ${account.Name}`}
                                data-testid={isService ? `button-view-svc-groups-${account.id}` : `button-view-groups-${account.id}`}
                            >
                                <Shield />
                                {accountGroups.length}
                            </Button>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="endpoint-overview ad-management">
            <div className="ad-layout" data-collapsed={isDomainSidebarCollapsed}>
                <aside className="overview-card ad-forest" aria-label="Domain forest">
                    <div className="ad-forest-head">
                        {!isDomainSidebarCollapsed && (
                            <div className="ad-forest-title">
                                <span className="ad-forest-title-icon" aria-hidden="true">
                                    <Building2 />
                                </span>
                                <span>Domain Forest</span>
                            </div>
                        )}
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setIsDomainSidebarCollapsed(!isDomainSidebarCollapsed)}
                            className="ad-icon-button"
                            aria-label={isDomainSidebarCollapsed ? "Expand domain forest" : "Collapse domain forest"}
                            data-testid="button-collapse-sidebar"
                        >
                            {isDomainSidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                        </Button>
                    </div>

                    {!isDomainSidebarCollapsed && (
                        <div className="ad-forest-list">
                            {generatedDomains.filter(d => d?.type === 'root').map(domain => domain && renderDomainTree(domain))}
                        </div>
                    )}
                </aside>

                <section className="ad-main">
                    <div className="overview-card ad-toolbar">
                        <div className="ad-toolbar-domain">
                            {selectedDomain && (
                                <>
                                    <span className="overview-heading-icon" aria-hidden="true">
                                        <Database />
                                    </span>
                                    <div className="min-w-0">
                                        <h2 className="ad-toolbar-title">{selectedDomain.fqdn}</h2>
                                        <p className="ad-toolbar-meta">
                                            {selectedDomain.userCount.toLocaleString()} users • {selectedDomain.serviceAccountCount.toLocaleString()} services
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                        <label className="ad-toolbar-search">
                            <span className="ad-search">
                                <Search className="ad-search-icon" aria-hidden="true" />
                                <Input
                                    placeholder="Search users, services, devices..."
                                    aria-label="Search users, services, devices"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    data-testid="input-search-domain"
                                />
                                <span className="ad-search-trailing">
                                    {isSearching ? (
                                        <Loader2 className="animate-spin" aria-label="Searching" />
                                    ) : searchQuery ? (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="ad-search-clear"
                                            onClick={() => setSearchQuery('')}
                                            aria-label="Clear search"
                                            data-testid="button-clear-search"
                                        >
                                            <X />
                                        </Button>
                                    ) : null}
                                </span>
                            </span>
                        </label>
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="ad-tabs-root">
                        <TabsList unstyled aria-label="Directory objects" className="cea-tabs ad-tabs">
                            {tabItems.map(({ value, label, icon: TabIcon, count, testId }) => (
                                <TabsTrigger
                                    key={value}
                                    unstyled
                                    value={value}
                                    className={`cea-tab ${activeTab === value ? "active" : ""}`}
                                    data-testid={testId}
                                >
                                    <TabIcon className="shrink-0" />
                                    {label}
                                    <span className="cea-tab-count">{count.toLocaleString()}</span>
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        <TabsContent value="users" className="ad-tab-panel mt-0">
                            <Card className="overview-card ad-table-card">
                                <div className="ad-table-head ad-grid-accounts">
                                    <div>Name</div>
                                    <div>Email</div>
                                    <div>Distinguished Name</div>
                                    <div className="text-right">Groups</div>
                                </div>
                                <div className="ad-table-body">
                                    {isLoadingUsers ? (
                                        <AdState loading message="Loading users..." />
                                    ) : isSearching ? (
                                        <AdState loading message="Searching..." />
                                    ) : paginatedUsers.length === 0 ? (
                                        <AdState
                                            icon={Users}
                                            title="No Users Found"
                                            message={debouncedSearch ? `No users match "${debouncedSearch}"` : 'No users available'}
                                        />
                                    ) : renderAccountRows(paginatedUsers, "user")}
                                </div>
                                {!isSearching && filteredUsers.length > 0 && renderPaginationControls(
                                    usersPage,
                                    totalUsersPages,
                                    filteredUsers.length,
                                    usersPageSize,
                                    setUsersPage,
                                    setUsersPageSize,
                                    'users'
                                )}
                            </Card>
                        </TabsContent>

                        <TabsContent value="services" className="ad-tab-panel mt-0">
                            <Card className="overview-card ad-table-card">
                                <div className="ad-table-head ad-grid-accounts">
                                    <div>Service Account</div>
                                    <div>Email</div>
                                    <div>Distinguished Name</div>
                                    <div className="text-right">Groups</div>
                                </div>
                                <div className="ad-table-body">
                                    {isLoadingUsers ? (
                                        <AdState loading message="Loading service accounts..." />
                                    ) : isSearching ? (
                                        <AdState loading message="Searching..." />
                                    ) : paginatedServiceAccounts.length === 0 ? (
                                        <AdState
                                            icon={Settings}
                                            title="No Service Accounts Found"
                                            message={debouncedSearch ? `No service accounts match "${debouncedSearch}"` : 'No service accounts available'}
                                        />
                                    ) : renderAccountRows(paginatedServiceAccounts, "service")}
                                </div>
                                {!isSearching && filteredServiceAccounts.length > 0 && renderPaginationControls(
                                    servicesPage,
                                    totalServicesPages,
                                    filteredServiceAccounts.length,
                                    servicesPageSize,
                                    setServicesPage,
                                    setServicesPageSize,
                                    'services'
                                )}
                            </Card>
                        </TabsContent>

                        <TabsContent value="ous" className="ad-tab-panel mt-0">
                            <Card className="overview-card ad-table-card">
                                <div className="ad-table-head ad-grid-ous">
                                    <div>Organization Unit</div>
                                    <div className="text-right">Actions</div>
                                </div>
                                <div className="ad-table-body">
                                    {isLoadingOUs ? (
                                        <AdState loading message="Loading organization units..." />
                                    ) : isSearching ? (
                                        <AdState loading message="Searching..." />
                                    ) : paginatedOUs.length === 0 ? (
                                        <AdState
                                            icon={Layers}
                                            title="No Organization Units Found"
                                            message={debouncedSearch ? `No OUs match "${debouncedSearch}"` : 'No organization units available'}
                                        />
                                    ) : (
                                        <div className="ad-rows">
                                            {paginatedOUs.map((ou) => (
                                                <div key={ou.id} className="ad-row ad-grid-ous" data-testid={`row-ou-${ou.id}`}>
                                                    <div className="ad-identity">
                                                        <span className="ad-row-icon" data-tone="amber" aria-hidden="true">
                                                            <Layers />
                                                        </span>
                                                        <div className="min-w-0">
                                                            <div className="ad-identity-name" data-plain="true">{ou.name}</div>
                                                            <div className="ad-cell-mono" title={ou.distinguishedName}>{ou.distinguishedName}</div>
                                                        </div>
                                                    </div>
                                                    <div className="ad-row-actions">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="ad-action-button"
                                                            onClick={() => handleShowOUUsers(ou)}
                                                            data-testid={`button-show-ou-users-${ou.id}`}
                                                        >
                                                            <Users />
                                                            Users
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="ad-action-button"
                                                            onClick={() => handleShowOUDevices(ou)}
                                                            data-testid={`button-show-ou-devices-${ou.id}`}
                                                        >
                                                            <Monitor />
                                                            Devices
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {!isSearching && filteredOUs.length > 0 && renderPaginationControls(
                                    ousPage,
                                    totalOUsPages,
                                    filteredOUs.length,
                                    ousPageSize,
                                    setOusPage,
                                    setOusPageSize,
                                    'ous'
                                )}
                            </Card>
                        </TabsContent>

                        <TabsContent value="devices" className="ad-tab-panel mt-0">
                            <Card className="overview-card ad-table-card">
                                <div className="ad-table-head ad-grid-devices">
                                    <div>Device</div>
                                    <div>Operating System</div>
                                    <div>Distinguished Name</div>
                                </div>
                                <div className="ad-table-body">
                                    {isLoadingDevices ? (
                                        <AdState loading message="Loading devices..." />
                                    ) : isSearching ? (
                                        <AdState loading message="Searching..." />
                                    ) : paginatedDevices.length === 0 ? (
                                        <AdState
                                            icon={Monitor}
                                            title="No Devices Found"
                                            message={debouncedSearch ? `No devices match "${debouncedSearch}"` : 'No devices available'}
                                        />
                                    ) : (
                                        <div className="ad-rows">
                                            {paginatedDevices.map((device, index) => (
                                                <div key={device.Name + index} className="ad-row ad-grid-devices" data-testid={`row-device-${device.Name}`}>
                                                    <div className="ad-identity">
                                                        <span className="ad-row-icon" aria-hidden="true">
                                                            <Monitor />
                                                        </span>
                                                        <div className="ad-identity-name" data-plain="true">{device.Name}</div>
                                                    </div>
                                                    <div className="ad-cell-muted">{device.OperatingSystem || '-'}</div>
                                                    <div className="ad-cell-mono" title={device.DistinguishedName}>{device.DistinguishedName}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {!isSearching && filteredDevices.length > 0 && renderPaginationControls(
                                    devicesPage,
                                    totalDevicesPages,
                                    filteredDevices.length,
                                    devicesPageSize,
                                    setDevicesPage,
                                    setDevicesPageSize,
                                    'devices'
                                )}
                            </Card>
                        </TabsContent>

                        <TabsContent value="groups" className="ad-tab-panel mt-0">
                            <Card className="overview-card ad-table-card">
                                <div className="ad-table-head ad-grid-groups">
                                    <div>Group Name</div>
                                    <div className="text-center">Members</div>
                                    <div className="text-right">Actions</div>
                                </div>
                                <div className="ad-table-body">
                                    {isSearching ? (
                                        <AdState loading message="Searching..." />
                                    ) : paginatedGroups.length === 0 ? (
                                        <AdState
                                            icon={Shield}
                                            title="No Groups Found"
                                            message={debouncedSearch ? `No groups match "${debouncedSearch}"` : 'No groups available'}
                                        />
                                    ) : (
                                        <div className="ad-rows">
                                            {paginatedGroups.map((membership) => (
                                                <div
                                                    key={membership.adUserGroup.id}
                                                    className="ad-row ad-grid-groups"
                                                    data-interactive="true"
                                                    data-testid={`row-group-${membership.adUserGroup.id}`}
                                                >
                                                    <div className="ad-identity">
                                                        <span className="ad-row-icon" aria-hidden="true">
                                                            <Shield />
                                                        </span>
                                                        <div className="min-w-0">
                                                            <div className="ad-identity-name" data-plain="true">{membership.adUserGroup.Name}</div>
                                                            <div className="ad-cell-mono" title={membership.adUserGroup.DistinguishedName}>
                                                                {membership.adUserGroup.DistinguishedName}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="ad-member-count">
                                                        <Users aria-hidden="true" />
                                                        {membership.adUserGroup.Members.length}
                                                    </div>
                                                    <div className="ad-row-actions">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="ad-action-button"
                                                            onClick={() => {
                                                                setSelectedGroupMembership(membership);
                                                                setShowGroupMembersModalOpen(true);
                                                            }}
                                                            data-testid={`button-show-users-${membership.adUserGroup.id}`}
                                                        >
                                                            <Users />
                                                            Users
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="ad-action-button"
                                                            data-tone="success"
                                                            onClick={() => {
                                                                setSelectedGroupMembership(membership);
                                                                setAddUserToGroupModalOpen(true);
                                                            }}
                                                            data-testid={`button-add-user-${membership.adUserGroup.id}`}
                                                        >
                                                            <UserPlus />
                                                            Add User
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {!isSearching && filteredGroups.length > 0 && renderPaginationControls(
                                    groupsPage,
                                    totalGroupsPages,
                                    filteredGroups.length,
                                    groupsPageSize,
                                    setGroupsPage,
                                    setGroupsPageSize,
                                    'groups'
                                )}
                            </Card>
                        </TabsContent>
                    </Tabs>
                </section>
            </div>

            <Dialog open={groupModalOpen} onOpenChange={setGroupModalOpen}>
                <DialogContent className="ad-dialog max-w-4xl">
                    <AdDialogHeader
                        icon={Users}
                        title={selectedGroupsList.length > 0
                            ? `Additional Groups (${selectedGroupsList.length})`
                            : `Users with ${selectedGroup.includes('OU=') ? 'OU' : 'Role'}: ${selectedGroup}`}
                        subtitle="Built-in directory membership"
                    />

                    <div className="ad-dialog-body">
                        {selectedGroupsList.length > 0 ? (
                            <div className="ad-dialog-chip-grid">
                                {selectedGroupsList.map((groupName, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className="ad-dialog-chip"
                                        onClick={() => {
                                            setSelectedGroup(groupName);
                                            setSelectedGroupsList([]);
                                        }}
                                        data-testid={`group-list-${groupName}`}
                                    >
                                        {groupName}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <>
                                <AdSearchInput
                                    value={groupUserSearch}
                                    onChange={setGroupUserSearch}
                                    placeholder="Search users..."
                                    testId="input-search-group-users"
                                />

                                <div className="ad-dialog-table">
                                    <div className="ad-dialog-table-head" style={{ gridTemplateColumns: '2fr 2fr 2fr' }}>
                                        <div>Name</div>
                                        <div>Email</div>
                                        <div>Login Status</div>
                                    </div>

                                    <div className="ad-dialog-rows">
                                        {(() => {
                                            const groupUsers = adUsers.filter((user: ADUser) =>
                                                user.DistinguishedName.includes(selectedGroup)
                                            ).filter(user =>
                                                groupUserSearch === '' ||
                                                user.Name.toLowerCase().includes(groupUserSearch.toLowerCase()) ||
                                                (user.EmailAddress || '').toLowerCase().includes(groupUserSearch.toLowerCase())
                                            );

                                            if (groupUsers.length === 0) {
                                                return (
                                                    <AdState
                                                        icon={Users}
                                                        title="No Users Found"
                                                        message="No users match the current search criteria."
                                                        compact
                                                    />
                                                );
                                            }

                                            return groupUsers.map((user) => (
                                                <div
                                                    key={user.id}
                                                    className="ad-dialog-row"
                                                    style={{ gridTemplateColumns: '2fr 2fr 2fr' }}
                                                    data-testid={`modal-user-${user.id}`}
                                                >
                                                    <div className="ad-identity">
                                                        <span className="ad-row-icon" data-size="sm" aria-hidden="true"><User /></span>
                                                        <span className="ad-identity-name" data-plain="true">{user.Name}</span>
                                                    </div>
                                                    <div className="ad-cell-muted">{user.EmailAddress || '-'}</div>
                                                    <div>
                                                        <span className="ad-status-pill" data-tone={user.loginStatus === 'LOGGED' ? 'success' : 'muted'}>
                                                            {user.loginStatus === 'LOGGED' ? 'Logged In' : 'Not Logged'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="ad-dialog-footer">
                        <Button
                            variant="outline"
                            className="ad-dialog-button"
                            onClick={() => setGroupModalOpen(false)}
                            data-testid="button-close-modal"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={addUserToGroupModalOpen} onOpenChange={setAddUserToGroupModalOpen}>
                <DialogContent className="ad-dialog max-w-2xl">
                    <AdDialogHeader
                        icon={UserPlus}
                        tone="success"
                        title="Add User to Group"
                        subtitle={selectedGroupMembership ? selectedGroupMembership.adUserGroup.Name : "Select a user to add"}
                    />

                    <div className="ad-dialog-body">
                        <AdSearchInput
                            value={userSearchForGroup}
                            onChange={setUserSearchForGroup}
                            placeholder="Search users to add..."
                            testId="input-search-add-user"
                        />

                        <div className="ad-dialog-table">
                            <div className="ad-dialog-rows">
                                {(() => {
                                    const currentMembers = selectedGroupMembership?.adUserGroup.Members || [];

                                    const groupDomain = selectedGroupMembership
                                        ? extractDomainFromDN(selectedGroupMembership.adUserGroup.DistinguishedName, new Set())
                                        : null;

                                    const uniqueUsers = Array.from(
                                        new Map(adUsers.map(user => [user.DistinguishedName.toLowerCase(), user])).values()
                                    );

                                    const availableUsers = uniqueUsers.filter(user => {
                                        if (groupDomain) {
                                            const userDomain = extractDomainFromDN(user.DistinguishedName, new Set());
                                            if (userDomain !== groupDomain) return false;
                                        }

                                        const isAlreadyMember = currentMembers.some(memberDN =>
                                            memberDN.toLowerCase() === user.DistinguishedName.toLowerCase()
                                        );

                                        const matchesSearch = userSearchForGroup === '' ||
                                            user.Name.toLowerCase().includes(userSearchForGroup.toLowerCase()) ||
                                            user.SamAccountName.toLowerCase().includes(userSearchForGroup.toLowerCase()) ||
                                            (user.EmailAddress || '').toLowerCase().includes(userSearchForGroup.toLowerCase());

                                        return !isAlreadyMember && matchesSearch;
                                    });

                                    if (availableUsers.length === 0) {
                                        return (
                                            <AdState
                                                icon={User}
                                                message={userSearchForGroup ? 'No matching users available to add' : 'All users are already members of this group'}
                                                compact
                                            />
                                        );
                                    }
                                    return availableUsers.map((user) => (
                                        <div
                                            key={user.id}
                                            className="ad-dialog-row ad-dialog-row-split"
                                            data-testid={`add-user-option-${user.id}`}
                                        >
                                            <div className="ad-identity">
                                                <span className="ad-row-icon" data-size="sm" aria-hidden="true"><User /></span>
                                                <div className="min-w-0">
                                                    <div className="ad-identity-name" data-plain="true">{user.Name}</div>
                                                    <div className="ad-identity-sub">{user.EmailAddress || user.SamAccountName}</div>
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                className="ad-row-primary"
                                                onClick={() => {
                                                    setSelectedUserToAdd(user);
                                                    setConfirmAddUserModalOpen(true);
                                                }}
                                                data-testid={`button-add-${user.id}`}
                                            >
                                                <UserPlus />
                                                Add
                                            </Button>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>

                    <div className="ad-dialog-footer">
                        <Button
                            variant="outline"
                            className="ad-dialog-button"
                            onClick={() => {
                                setAddUserToGroupModalOpen(false);
                                setUserSearchForGroup("");
                            }}
                            data-testid="button-cancel-add-user"
                        >
                            Cancel
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={confirmAddUserModalOpen} onOpenChange={setConfirmAddUserModalOpen}>
                <DialogContent className="ad-dialog ad-dialog-confirm max-w-md">
                    <AdDialogHeader
                        icon={UserPlus}
                        tone="success"
                        title="Confirm Add User"
                        subtitle="Are you sure you want to add this user to the group?"
                    />

                    <div className="ad-dialog-body">
                        {selectedUserToAdd && (
                            <div className="ad-confirm-entity">
                                <span className="ad-row-icon" aria-hidden="true"><User /></span>
                                <div className="min-w-0">
                                    <div className="ad-identity-name" data-plain="true">{selectedUserToAdd.Name}</div>
                                    <div className="ad-identity-sub">{selectedUserToAdd.EmailAddress || selectedUserToAdd.SamAccountName}</div>
                                </div>
                            </div>
                        )}

                        {selectedGroupMembership && (
                            <div className="ad-confirm-target" data-tone="success">
                                <Shield aria-hidden="true" />
                                <span>To group <strong>{selectedGroupMembership.adUserGroup.Name}</strong></span>
                            </div>
                        )}
                    </div>

                    <div className="ad-dialog-footer">
                        <Button
                            variant="outline"
                            className="ad-dialog-button"
                            onClick={() => {
                                setConfirmAddUserModalOpen(false);
                                setSelectedUserToAdd(null);
                            }}
                            disabled={isAddingUser}
                            data-testid="button-cancel-confirm-add"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="ad-dialog-button"
                            data-tone="success"
                            onClick={() => {
                                if (selectedGroupMembership && selectedUserToAdd) {
                                    handleAddUserToGroup(selectedGroupMembership.adUserGroup.id, selectedUserToAdd);
                                    setConfirmAddUserModalOpen(false);
                                    setSelectedUserToAdd(null);
                                }
                            }}
                            disabled={isAddingUser}
                            data-testid="button-confirm-add"
                        >
                            {isAddingUser ? (
                                <>
                                    <Loader2 className="animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                "Confirm"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={removeUserModalOpen} onOpenChange={setRemoveUserModalOpen}>
                <DialogContent className="ad-dialog ad-dialog-confirm max-w-md">
                    <AdDialogHeader
                        icon={AlertTriangle}
                        tone="danger"
                        title="Remove User from Group"
                        subtitle="Are you sure you want to remove this user from the group?"
                    />

                    <div className="ad-dialog-body">
                        {selectedMemberToRemove && (
                            <div className="ad-confirm-entity">
                                <span className="ad-row-icon" data-tone="muted" aria-hidden="true"><User /></span>
                                <div className="min-w-0">
                                    <div className="ad-identity-name" data-plain="true">{extractCNFromDN(selectedMemberToRemove)}</div>
                                    <div className="ad-cell-mono ad-wrap">{selectedMemberToRemove}</div>
                                </div>
                            </div>
                        )}

                        {selectedGroupMembership && (
                            <div className="ad-confirm-target" data-tone="danger">
                                <Shield aria-hidden="true" />
                                <span>From group <strong>{selectedGroupMembership.adUserGroup.Name}</strong></span>
                            </div>
                        )}
                    </div>

                    <div className="ad-dialog-footer">
                        <Button
                            variant="outline"
                            className="ad-dialog-button"
                            onClick={() => {
                                setRemoveUserModalOpen(false);
                                setSelectedMemberToRemove("");
                            }}
                            disabled={isRemovingUser}
                            data-testid="button-cancel-remove"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            className="ad-dialog-button"
                            onClick={() => selectedGroupMembership && handleRemoveUserFromGroup(selectedGroupMembership.adUserGroup.id, selectedMemberToRemove)}
                            disabled={isRemovingUser}
                            data-testid="button-confirm-remove"
                        >
                            {isRemovingUser ? (
                                <>
                                    <Loader2 className="animate-spin" />
                                    Removing...
                                </>
                            ) : (
                                "Remove User"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                open={userGroupsModalOpen}
                onOpenChange={(open) => {
                    setUserGroupsModalOpen(open);
                    if (!open) {
                        setSelectedUserForGroups(null);
                        setUserGroupsSearch("");
                    }
                }}
            >
                <DialogContent className="ad-dialog max-w-3xl">
                    <AdDialogHeader
                        icon={Shield}
                        title="Group Memberships"
                        subtitle={selectedUserForGroups ? `for ${selectedUserForGroups.Name}` : undefined}
                        detail={selectedUserForGroups ? `${getUserGroupsForUser(selectedUserForGroups).length} groups` : undefined}
                    />

                    <div className="ad-dialog-body">
                        {selectedUserForGroups && (
                            <>
                                <div className="ad-confirm-entity">
                                    <span className="ad-row-icon" aria-hidden="true"><User /></span>
                                    <div className="min-w-0">
                                        <div className="ad-identity-name" data-plain="true">{selectedUserForGroups.Name}</div>
                                        <div className="ad-identity-sub">{selectedUserForGroups.EmailAddress || selectedUserForGroups.SamAccountName}</div>
                                    </div>
                                </div>

                                <AdSearchInput
                                    value={userGroupsSearch}
                                    onChange={setUserGroupsSearch}
                                    placeholder="Search group memberships..."
                                    testId="input-search-user-groups"
                                />

                                <div className="ad-dialog-table">
                                    <div className="ad-dialog-rows">
                                        {(() => {
                                            const userGroups = getUserGroupsForUser(selectedUserForGroups);

                                            const filteredUserGroups = userGroups.filter(m => {
                                                if (!userGroupsSearch) return true;
                                                const query = userGroupsSearch.toLowerCase();

                                                const name = (m.adUserGroup.Name || "").toLowerCase();
                                                const sam = (m.adUserGroup.SamAccountName || "").toLowerCase();
                                                const dn = (m.adUserGroup.DistinguishedName || "").toLowerCase();
                                                const cn = (m.adUserGroup.cn || "").toLowerCase();
                                                const id = String(m.adUserGroup.id || "").toLowerCase();
                                                const membersJoined = m.adUserGroup.Members.join(" ").toLowerCase();

                                                return (
                                                    name.includes(query) ||
                                                    sam.includes(query) ||
                                                    dn.includes(query) ||
                                                    cn.includes(query) ||
                                                    id.includes(query) ||
                                                    membersJoined.includes(query)
                                                );
                                            });
                                            if (filteredUserGroups.length === 0) {
                                                return (
                                                    <AdState
                                                        icon={Shield}
                                                        title="No Group Memberships"
                                                        message="This user is not a member of any groups."
                                                        compact
                                                    />
                                                );
                                            }

                                            return filteredUserGroups.map((membership) => (
                                                <div
                                                    key={membership.adUserGroup.id}
                                                    className="ad-dialog-row ad-dialog-row-split"
                                                    data-testid={`user-group-${membership.adUserGroup.id}`}
                                                >
                                                    <div className="ad-identity ad-identity-top">
                                                        <span className="ad-row-icon" aria-hidden="true"><Shield /></span>
                                                        <div className="min-w-0">
                                                            <div className="ad-identity-name" data-plain="true">{membership.adUserGroup.Name}</div>
                                                            <div className="ad-cell-mono" title={membership.adUserGroup.DistinguishedName}>
                                                                {membership.adUserGroup.SamAccountName}
                                                            </div>
                                                            <div className="ad-member-count" data-align="start">
                                                                <Users aria-hidden="true" />
                                                                {membership.adUserGroup.Members.length} members
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="ad-action-button"
                                                        data-tone="danger"
                                                        onClick={() => {
                                                            setSelectedGroupMembership(membership);
                                                            setSelectedMemberToRemove(selectedUserForGroups!.DistinguishedName);
                                                            setRemoveUserModalOpen(true);
                                                        }}
                                                        data-testid={`button-remove-from-group-${membership.adUserGroup.id}`}
                                                    >
                                                        <UserMinus />
                                                        Remove
                                                    </Button>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="ad-dialog-footer">
                        <Button
                            variant="outline"
                            className="ad-dialog-button"
                            onClick={() => {
                                setUserGroupsModalOpen(false);
                                setSelectedUserForGroups(null);
                                setUserGroupsSearch("");
                            }}
                            data-testid="button-close-user-groups"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={showGroupMembersModalOpen} onOpenChange={(open) => {
                setShowGroupMembersModalOpen(open);
                if (!open) setGroupMembersSearch("");
            }}>
                <DialogContent className="ad-dialog max-w-4xl">
                    <AdDialogHeader
                        icon={Shield}
                        title="Group Members"
                        subtitle={selectedGroupMembership?.adUserGroup.Name}
                        detail={selectedGroupMembership ? `${selectedGroupMembership.adUserGroup.Members.length} members` : undefined}
                    />

                    <div className="ad-dialog-body">
                        {selectedGroupMembership && (
                            <>
                                <AdSearchInput
                                    value={groupMembersSearch}
                                    onChange={setGroupMembersSearch}
                                    placeholder="Search members..."
                                    testId="input-search-group-members"
                                />

                                <div className="ad-dialog-table">
                                    <div className="ad-dialog-table-head" style={{ gridTemplateColumns: '2fr 2fr 1fr' }}>
                                        <div>Name</div>
                                        <div>Account</div>
                                        <div className="text-right">Actions</div>
                                    </div>
                                    <div className="ad-dialog-rows">
                                        {(() => {
                                            const members = selectedGroupMembership.adUserGroup.Members
                                                .filter(memberDN => {
                                                    if (!groupMembersSearch) return true;
                                                    const cn = extractCNFromDN(memberDN);
                                                    return cn.toLowerCase().includes(groupMembersSearch.toLowerCase()) ||
                                                        memberDN.toLowerCase().includes(groupMembersSearch.toLowerCase());
                                                });

                                            if (members.length === 0) {
                                                return (
                                                    <AdState
                                                        icon={User}
                                                        message={groupMembersSearch ? 'No members match your search' : 'No members in this group'}
                                                        compact
                                                    />
                                                );
                                            }

                                            return members.map((memberDN, idx) => {
                                                const memberName = extractCNFromDN(memberDN);
                                                const samMatch = memberDN.match(/CN=([^,]+)/);
                                                const samName = samMatch ? samMatch[1].toLowerCase().replace(/\s+/g, '.') : memberName.toLowerCase();

                                                return (
                                                    <div
                                                        key={idx}
                                                        className="ad-dialog-row"
                                                        style={{ gridTemplateColumns: '2fr 2fr 1fr' }}
                                                        data-testid={`group-member-${idx}`}
                                                    >
                                                        <div className="ad-identity">
                                                            <span className="ad-row-icon" data-size="sm" aria-hidden="true"><User /></span>
                                                            <span className="ad-identity-name" data-plain="true">{memberName}</span>
                                                        </div>
                                                        <div className="ad-cell-mono" title={memberDN}>{samName}</div>
                                                        <div className="ad-cell-end">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="ad-action-button"
                                                                data-tone="danger"
                                                                onClick={() => {
                                                                    setSelectedMemberToRemove(memberDN);
                                                                    setRemoveUserModalOpen(true);
                                                                }}
                                                                data-testid={`button-remove-member-${idx}`}
                                                            >
                                                                <UserMinus />
                                                                Remove
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="ad-dialog-footer">
                        <Button
                            variant="outline"
                            className="ad-dialog-button"
                            onClick={() => {
                                setShowGroupMembersModalOpen(false);
                                setGroupMembersSearch("");
                            }}
                            data-testid="button-close-group-members"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* =====================================================================
          OU Users Modal
          =====================================================================
          Displays users within a selected Organization Unit.
          
          API: GET /api/organization-units/{OU-ID}/users
          Example: GET https://phanydev1.whiteswansec.io/api/organization-units/1/users
          
          Response: Array of ADUser objects with fields:
          - id: Unique identifier
          - Name: Display name (e.g., "SHEENA_NICHOLS")
          - SamAccountName: SAM account name
          - DistinguishedName: Full AD path
          - isServiceAccount: Boolean (typically false)
          - loginStatus: "LOGGED" or "NOT_LOGGED"
          - EmailAddress, UserPrincipalName, MobilePhone (nullable)
          ===================================================================== */}
            <Dialog open={ouUsersModalOpen} onOpenChange={(open) => {
                setOuUsersModalOpen(open);
                if (!open) {
                    setOuUsersSearch("");
                    setSelectedOU(null);
                    setOuUsers([]);
                }
            }}>
                <DialogContent className="ad-dialog max-w-4xl">
                    <AdDialogHeader
                        icon={Layers}
                        tone="warning"
                        title="OU Users"
                        subtitle={selectedOU?.name}
                        detail={selectedOU ? `${ouUsers.length} users` : undefined}
                    />

                    <div className="ad-dialog-body">
                        {selectedOU && (
                            <>
                                <AdSearchInput
                                    value={ouUsersSearch}
                                    onChange={setOuUsersSearch}
                                    placeholder="Search users..."
                                    testId="input-search-ou-users"
                                />

                                <div className="ad-dialog-table">
                                    <div className="ad-dialog-table-head" style={{ gridTemplateColumns: '2fr 2fr 2fr' }}>
                                        <div>Name</div>
                                        <div>Account</div>
                                        <div>UPN</div>
                                    </div>
                                    <div className="ad-dialog-rows">
                                        {(() => {
                                            const filteredOuUsers = ouUsers.filter(user => {
                                                if (!ouUsersSearch) return true;
                                                const query = ouUsersSearch.toLowerCase();
                                                return user.Name.toLowerCase().includes(query) ||
                                                    user.SamAccountName.toLowerCase().includes(query) ||
                                                    (user.UserPrincipalName || '').toLowerCase().includes(query) ||
                                                    user.DistinguishedName.toLowerCase().includes(query);
                                            });

                                            if (filteredOuUsers.length === 0) {
                                                return (
                                                    <AdState
                                                        icon={User}
                                                        message={ouUsersSearch ? 'No users match your search' : 'No users in this OU'}
                                                        compact
                                                    />
                                                );
                                            }

                                            return filteredOuUsers.map((user) => (
                                                <div
                                                    key={user.id}
                                                    className="ad-dialog-row"
                                                    style={{ gridTemplateColumns: '2fr 2fr 2fr' }}
                                                    data-testid={`ou-user-${user.id}`}
                                                >
                                                    <div className="ad-identity">
                                                        <span className="ad-row-icon" data-size="sm" aria-hidden="true"><User /></span>
                                                        <span className="ad-identity-name" data-plain="true">{user.Name}</span>
                                                    </div>
                                                    <div className="ad-cell-mono">{user.SamAccountName}</div>
                                                    <div className="ad-cell-muted" title={user.UserPrincipalName || ''}>
                                                        {user.UserPrincipalName || '-'}
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="ad-dialog-footer">
                        <Button
                            variant="outline"
                            className="ad-dialog-button"
                            onClick={() => {
                                setOuUsersModalOpen(false);
                                setOuUsersSearch("");
                                setSelectedOU(null);
                                setOuUsers([]);
                            }}
                            data-testid="button-close-ou-users"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* =====================================================================
          OU Devices Modal
          =====================================================================
          Displays devices/computers within a selected Organization Unit.
          
          API: GET /api/organization-units/{OU-ID}/computers
          Example: GET https://phanydev1.whiteswansec.io/api/organization-units/1/computers
          
          Response: Array of ADDevice objects with fields:
          - endpointId: Endpoint identifier (nullable)
          - hasAgent: Boolean indicating if agent is installed
          - os: OS type shorthand (e.g., "WINDOWS", nullable)
          - DNSHostName: Full DNS hostname (nullable)
          - DistinguishedName: Full AD path
          - IPv4Address: IP address (nullable)
          - Name: Device name
          - OperatingSystem: Full OS name (nullable)
          ===================================================================== */}
            <Dialog open={ouDevicesModalOpen} onOpenChange={(open) => {
                setOuDevicesModalOpen(open);
                if (!open) {
                    setOuDevicesSearch("");
                    setSelectedOUForDevices(null);
                    setOuDevices([]);
                }
            }}>
                <DialogContent className="ad-dialog max-w-4xl">
                    <AdDialogHeader
                        icon={Monitor}
                        title="OU Devices"
                        subtitle={selectedOUForDevices?.name}
                        detail={selectedOUForDevices ? `${ouDevices.length} devices` : undefined}
                    />

                    <div className="ad-dialog-body">
                        {selectedOUForDevices && (
                            <>
                                <AdSearchInput
                                    value={ouDevicesSearch}
                                    onChange={setOuDevicesSearch}
                                    placeholder="Search devices..."
                                    testId="input-search-ou-devices"
                                />

                                <div className="ad-dialog-table">
                                    <div className="ad-dialog-table-head" style={{ gridTemplateColumns: '2fr 2fr 2fr 1fr' }}>
                                        <div>Name</div>
                                        <div>OS</div>
                                        <div>IP Address</div>
                                        <div>Agent</div>
                                    </div>
                                    <div className="ad-dialog-rows">
                                        {(() => {
                                            const filteredOuDevices = ouDevices.filter(device => {
                                                if (!ouDevicesSearch) return true;
                                                const query = ouDevicesSearch.toLowerCase();
                                                return device.Name.toLowerCase().includes(query) ||
                                                    (device.DNSHostName || '').toLowerCase().includes(query) ||
                                                    (device.OperatingSystem || '').toLowerCase().includes(query) ||
                                                    (device.IPv4Address || '').toLowerCase().includes(query) ||
                                                    device.DistinguishedName.toLowerCase().includes(query);
                                            });

                                            if (filteredOuDevices.length === 0) {
                                                return (
                                                    <AdState
                                                        icon={Monitor}
                                                        message={ouDevicesSearch ? 'No devices match your search' : 'No devices in this OU'}
                                                        compact
                                                    />
                                                );
                                            }

                                            return filteredOuDevices.map((device, idx) => (
                                                <div
                                                    key={`${device.Name}-${idx}`}
                                                    className="ad-dialog-row"
                                                    style={{ gridTemplateColumns: '2fr 2fr 2fr 1fr' }}
                                                    data-testid={`ou-device-${device.Name}`}
                                                >
                                                    <div className="ad-identity">
                                                        <span className="ad-row-icon" data-size="sm" aria-hidden="true"><Monitor /></span>
                                                        <span className="ad-identity-name" data-plain="true">{device.Name}</span>
                                                    </div>
                                                    <div className="ad-cell-muted" title={device.OperatingSystem || ''}>
                                                        {device.OperatingSystem || device.os || '-'}
                                                    </div>
                                                    <div className="ad-cell-mono">{device.IPv4Address || '-'}</div>
                                                    <div>
                                                        <span className="ad-status-pill" data-tone={device.hasAgent ? 'success' : 'muted'}>
                                                            {device.hasAgent ? 'Active' : 'None'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="ad-dialog-footer">
                        <Button
                            variant="outline"
                            className="ad-dialog-button"
                            onClick={() => {
                                setOuDevicesModalOpen(false);
                                setOuDevicesSearch("");
                                setSelectedOUForDevices(null);
                                setOuDevices([]);
                            }}
                            data-testid="button-close-ou-devices"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
